// Theme management — przechowujemy wybór w localStorage pod kluczem "theme".
// Wartości: "light" | "dark" | "system". Domyślnie "dark" (oryginalna paleta).
// Faktyczna klasa .dark na <html> ustawiana jest przez inline-skrypt w <head>
// (patrz __root.tsx) jeszcze przed pierwszym renderem, żeby nie było FOUC.

import { useEffect, useState } from "react";

export type Theme = "light" | "dark" | "system";
export const THEME_STORAGE_KEY = "theme";

export function resolveTheme(theme: Theme): "light" | "dark" {
  if (theme === "system") {
    if (typeof window === "undefined") return "dark";
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return theme;
}

export function applyTheme(theme: Theme) {
  if (typeof document === "undefined") return;
  const resolved = resolveTheme(theme);
  document.documentElement.classList.toggle("dark", resolved === "dark");
  document.documentElement.style.colorScheme = resolved;
}

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {}
  return "dark";
}

export function useTheme() {
  // Pierwszy render klienta musi być identyczny z SSR ("dark"). Dopiero po
  // hydratacji czytamy localStorage, inaczej ThemeToggle renderuje inną ikonę
  // i potrafi zerwać hydratację całej strony.
  const [theme, setThemeState] = useState<Theme>("dark");

  useEffect(() => {
    const stored = readStoredTheme();
    setThemeState(stored);
    applyTheme(stored);
  }, []);

  // Reaguj na zmiany preferencji systemu, gdy użytkownik wybrał "system".
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("system");
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [theme]);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, t);
    } catch {}
    applyTheme(t);
    void import("@/lib/analytics").then(({ trackTheme }) => trackTheme(resolveTheme(t))).catch(() => {});
  };

  return { theme, resolved: resolveTheme(theme), setTheme };
}
